import React, { useState, useEffect, useCallback } from 'react';
import './styles/TableSettingModal.css';
import CommonButton from '../elments/CommonButton';
import TableComponent from '../elments/TableComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';

const TableSettingModal = ({ isOpen, rows, onClose, onRowsChange, typeofproperty, netLabel, setNetLabel, createNet, showRowFixNoRank, inline = false }) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowsLabel, setSelectedRowsLabel] = useState([]);
  const [reverseLabels, setReverseLabels] = useState(false);
  const [exclusive, setExclusive] = useState(false);
  const [textFormatMode, setTextFormatMode] = useState(false);
  const [textFormatValue, setTextFormatValue] = useState('');
  const [textFormatError, setTextFormatError] = useState('');
  const [textFormatBaseline, setTextFormatBaseline] = useState([]);
  const safeRows = Array.isArray(rows) ? rows : [];
  // Function to handle logic change
  const handleLogicChange = (index, newLogic) => {
    const updatedRows = safeRows.map((row, i) =>
      i === index ? { ...row, Logic: newLogic } : row
    );
    onRowsChange(updatedRows);
  };

  const handleRowSelectionChange = (rowIndex, isSelected) => {
    const row = rows[rowIndex];
    const newSelectedRows = isSelected 
      ? [...selectedRows, row.index] 
      : selectedRows.filter(r => r !== row.index);
    const newSelectedRowsText = isSelected 
      ? [...selectedRowsLabel, row] 
      : selectedRowsLabel.filter(r => r.index !== row.index);
    
    setSelectedRows(newSelectedRows);
    setSelectedRowsLabel(newSelectedRowsText);
    setNetLabel("NET: " + newSelectedRowsText.map(item => item.title).join(' / '));
  };

  const handlePropertyChange = (index, newValue) => {
    const updatedRows = rows.map((row, i) =>
      i === index ? { ...row, title: newValue } : row
    );
    onRowsChange(updatedRows);
  };

  const handleFactorChange = (index, newFactor) => {
    const updatedRows = rows.map((row, i) =>
      i === index ? { ...row, factor: newFactor } : row
    );
    onRowsChange(updatedRows);
  };

  const handleBaseTextChange = (index, newBaseText) => {
    const updatedRows = rows.map((row, i) =>
      i === index ? { ...row, baseText: newBaseText } : row
    );
    onRowsChange(updatedRows);
  };

  // Index is a free-form identifier (real rows/values carry their SPSS-derived
  // positive index; custom/net-style entries use the auto-decrementing
  // negative scheme — see NEW_ROW_INDEX_START in PropertiesComponent). Stored
  // as typed so a still-being-edited "-" or blank value isn't clobbered.
  const handleIndexChange = (index, newIndexRaw) => {
    const updatedRows = rows.map((row, i) =>
      i === index ? { ...row, index: newIndexRaw === '' ? newIndexRaw : (Number(newIndexRaw) || newIndexRaw) } : row
    );
    onRowsChange(updatedRows);
  };

  // Function for TableComponent's checkbox property change
  const handleTablePropertyChange = (property, itemType, index, value) => {
    if (property === 'select') {
      // Handle row selection
      handleRowSelectionChange(index, value);
    } else {
      // Handle other properties
      const updatedRows = rows.map((row, i) =>
        i === index ? { ...row, [property]: value } : row
      );
      onRowsChange(updatedRows);
    }
  };

  const handlePaste = (index, event, field) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData('text');
    const pastedRows = pasteData.split('\n').map(row => row.trim()).filter(row => row !== '');
  
    const updatedRows = [...rows];
    pastedRows.forEach((pastedRow, i) => {
      const rowIndex = index + i;
      if (rowIndex < updatedRows.length) {
        updatedRows[rowIndex] = { ...updatedRows[rowIndex], [field]: pastedRow };
      }
    });
    onRowsChange(updatedRows);
  };
  
  const handleDeleteRow = (index) => {
    const updatedRows = rows.filter((_, i) => i !== index);
    onRowsChange(updatedRows);
  };

  const moveRow = (fromIndex, toIndex) => {
    if (toIndex >= 0 && toIndex < rows.length) {
      const updatedRows = [...rows];
      const movedRow = updatedRows.splice(fromIndex, 1)[0];
      updatedRows.splice(toIndex, 0, movedRow);
      onRowsChange(updatedRows);
    }
  };

  const handleKeyDown = useCallback((event) => {
    if (selectedRows.length === 0) return;

    if (event.key === 'ArrowUp') {
      // Find the index of the first selected row
      const selectedIndex = rows.findIndex(row => row.index === selectedRows[0]);
      if (selectedIndex > 0) {
        moveRow(selectedIndex, selectedIndex - 1);
      }
    } else if (event.key === 'ArrowDown') {
      const selectedIndex = rows.findIndex(row => row.index === selectedRows[0]);
      if (selectedIndex < rows.length - 1) {
        moveRow(selectedIndex, selectedIndex + 1);
      }
    }
  }, [rows, selectedRows, onRowsChange]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  const reverseValueLabels = () => {
    const updatedRows = [...rows].sort((a, b) => Number(b.index) - Number(a.index));
    onRowsChange(updatedRows);
  };

  useEffect(() => {
    if (reverseLabels) {
      reverseValueLabels();
    }
  }, [reverseLabels]);

  useEffect(() => {
    if (isOpen) {
      setTextFormatMode(false);
      setTextFormatValue('');
      setTextFormatError('');
      setTextFormatBaseline([]);
    }
  }, [isOpen]);

  // The full set of data properties the table structure defines for a row,
  // in the same left-to-right order as the columns — this is fixed by the
  // table's shape, not by which fields any individual row happens to have
  // set, so it must be used (rather than scanning rows) to know a column
  // exists even when every row leaves it blank/untouched.
  const canonicalRowKeys = [
    'index',
    'title',
    'Logic',
    'factor',
    'baseText',
    ...((typeofproperty === 'values' || (showRowFixNoRank && typeofproperty === 'rows')) ? ['norank', 'fix'] : []),
    ...(typeofproperty === 'values' ? ['summaryValue'] : []),
  ];

  // Converts the current rows into a copy-pasteable "text/object" format —
  // one JSON object per line so lines (rows) can be freely reordered,
  // including pasted back bottom-to-top, without breaking the others.
  // Every row is normalized to the canonical column keys plus any extra
  // ad-hoc keys found across all rows, so blank/missing properties still
  // show up instead of being silently dropped.
  const rowsToTextFormat = (rowsArr) => {
    const allKeys = [...canonicalRowKeys];
    const seenKeys = new Set(canonicalRowKeys);
    rowsArr.forEach(row => {
      Object.keys(row).forEach(key => {
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          allKeys.push(key);
        }
      });
    });

    return rowsArr
      .map(row => {
        const normalizedRow = {};
        allKeys.forEach(key => {
          const value = row[key];
          normalizedRow[key] = value === undefined ? '' : value;
        });
        return JSON.stringify(normalizedRow);
      })
      .join('\n');
  };

  // Parses the Text Format textarea back into rows. `baselineIds` is the
  // row order (by identity/index) captured when Text Format view was
  // opened — it lets us tell a freshly-pasted copy of a stub apart from
  // the stale original left behind, so copy/paste of a single stub moves
  // it instead of wiping out every other row.
  const parseTextFormat = (text, baselineIds = []) => {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line !== '');
    if (lines.length === 0) {
      return { error: 'No data found. Please add or paste the Text Format data.' };
    }
    const parsedRows = [];
    for (let i = 0; i < lines.length; i++) {
      let obj;
      try {
        obj = JSON.parse(lines[i]);
      } catch (err) {
        return { error: `Invalid Text Format on line ${i + 1}. Each row must look like {"index":0,"title":"Example"}.` };
      }
      if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
        return { error: `Invalid Text Format on line ${i + 1}. Each row must be an object, e.g. {"index":0,"title":"Example"}.` };
      }
      parsedRows.push(obj);
    }

    // Map each identity (row.index) to the identity that preceded it in
    // the baseline order, so we can recognize which duplicate occurrence
    // is the untouched original vs. the newly pasted/moved one.
    const baselinePred = new Map();
    baselineIds.forEach((id, i) => {
      baselinePred.set(id, i === 0 ? null : baselineIds[i - 1]);
    });

    const occurrencesById = new Map();
    parsedRows.forEach((row, i) => {
      const id = row.index;
      if (id === undefined) return;
      if (!occurrencesById.has(id)) occurrencesById.set(id, []);
      occurrencesById.get(id).push(i);
    });

    const dropPositions = new Set();
    occurrencesById.forEach((positions, id) => {
      if (positions.length < 2) return; // no duplicate for this stub

      const hasBaseline = baselinePred.has(id);
      const expectedPred = baselinePred.get(id);
      const movedCandidates = positions.filter(pos => {
        const predId = pos === 0 ? null : parsedRows[pos - 1].index;
        return !hasBaseline || predId !== expectedPred;
      });

      // Exactly one occurrence no longer sits after its original
      // predecessor — that's the moved/pasted one; drop the rest.
      const keepPos = movedCandidates.length === 1
        ? movedCandidates[0]
        : positions[positions.length - 1]; // ambiguous fallback: keep latest

      positions.forEach(pos => {
        if (pos !== keepPos) dropPositions.add(pos);
      });
    });

    const finalRows = parsedRows.filter((_, i) => !dropPositions.has(i));
    return { rows: finalRows };
  };

  const handleTextFormatToggle = () => {
    if (textFormatMode) {
      // "Table Format" clicked — parse the (possibly edited/pasted) text
      // and generate the table from it. Stay in Text Format on error so
      // nothing gets corrupted.
      const result = parseTextFormat(textFormatValue, textFormatBaseline);
      if (result.error) {
        setTextFormatError(result.error);
        return;
      }
      setTextFormatError('');
      onRowsChange(result.rows);
      setTextFormatMode(false);
    } else {
      // "Text Format" clicked — snapshot the current rows as text.
      setTextFormatValue(rowsToTextFormat(safeRows));
      setTextFormatBaseline(safeRows.map(row => row.index));
      setTextFormatError('');
      setTextFormatMode(true);
    }
  };

  const handleTextFormatChange = (event) => {
    setTextFormatValue(event.target.value);
    if (textFormatError) setTextFormatError('');
  };

  const handleExclusive = () => {
    const updatedRows = rows.map(row =>
      selectedRows.includes(row.index) ? { ...row, exclusive: true } : row
    );
    onRowsChange(updatedRows);
    setExclusive(false);
    setSelectedRows([]);
    setSelectedRowsLabel([]);
  };

  if (!isOpen) return null;

  // Define custom columns for TableComponent with all required functionality
  const columns = [
    {
      key: 'index',
      label: 'Index',
      width: '8%',
      render: (item, index) => (
        <input
          type="text"
          value={item.index ?? index}
          onChange={(e) => handleIndexChange(index, e.target.value)}
          className="index-input"
          style={{ width: '100%' }}
        />
      )
    },
    { 
      key: 'label', 
      label: 'Label', 
      width: '25%',
      render: (item, index) => (
        <input
          type="text"
          value={item.title}
          title={item.title}
          onChange={(e) => handlePropertyChange(index, e.target.value)}
          onPaste={(e) => handlePaste(index, e, 'title')}
          className="title-input"
        />
      )
    },
    { 
      key: 'logic', 
      label: 'Logic', 
      width: '20%',
      render: (item, index) => (
        <input
          type="text"
          value={item.Logic || ''}
          onChange={(e) => handleLogicChange(index, e.target.value)}
          onPaste={(e) => handlePaste(index, e, 'Logic')}
          placeholder="Logic"
          className="logic-input"
        />
      )
    },
    {
      key: 'factor',
      label: 'Factor',
      width: '15%',
      render: (item, index) => (
        <input
          type="text"
          value={item.factor || ''}
          onChange={(e) => handleFactorChange(index, e.target.value)}
          onPaste={(e) => handlePaste(index, e, 'factor')}
          className="factor-input"
        />
      )
    },
    {
      key: 'baseText',
      label: 'Base Text',
      width: '15%',
      render: (item, index) => (
        <input
          type="text"
          value={item.baseText || ''}
          onChange={(e) => handleBaseTextChange(index, e.target.value)}
          onPaste={(e) => handlePaste(index, e, 'baseText')}
          placeholder="Override base title..."
          className="factor-input"
        />
      )
    },
    {
      key: 'select',
      label: 'Select',
      width: '7%',
      type: 'checkbox',
      render: (item, index) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(item.index)}
          onChange={(e) => handleRowSelectionChange(index, e.target.checked)}
          className="row-select-checkbox"
        />
      )
    },
    ...(typeofproperty === 'values' || (showRowFixNoRank && typeofproperty === 'rows') ? [
      {
        key: 'norank',
        label: 'No Rank',
        width: '7%',
        render: (item, index) => (
          <input
            type="checkbox"
            title="Exclude from ranking (keep at bottom)"
            checked={!!item.norank}
            onChange={(e) => handleTablePropertyChange('norank', typeofproperty, index, e.target.checked)}
          />
        )
      },
      {
        key: 'fix',
        label: 'Fix Pos',
        width: '7%',
        render: (item, index) => (
          <input
            type="checkbox"
            title="Keep this row at its original position"
            checked={!!item.fix}
            onChange={(e) => handleTablePropertyChange('fix', typeofproperty, index, e.target.checked)}
          />
        )
      }
    ] : []),
    ...(typeofproperty === 'values' ? [
      {
        key: 'summaryValue',
        label: 'In Summary',
        width: '7%',
        render: (item, index) => (
          <input
            type="checkbox"
            title="Include this value in the Summary Net export when Summary Net is on"
            checked={!!item.summaryValue}
            onChange={(e) => handleTablePropertyChange('summaryValue', typeofproperty, index, e.target.checked)}
          />
        )
      }
    ] : []),
    {
      key: 'delete',
      label: '',
      width: '5%',
      render: (item, index) => (
        <button
          onClick={() => handleDeleteRow(index)}
          title="Delete this row"
          style={{
            background: 'none',
            border: 'none',
            color: '#ef4444',
            cursor: 'pointer',
            fontSize: 14,
            padding: '2px 5px',
            borderRadius: 3,
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          ✕
        </button>
      )
    },
    {
      key: 'move',
      label: 'Move',
      width: '15%',
      render: (item, index) => (
        <div className="move-buttons">
          <CommonButton
            title="Move Row Up"
            label=""
            symbol={faArrowUp}
            onClick={() => moveRow(index, index - 1)}
            disabled={index === 0}
            variant="arrow"
            size="small"
          />
          <CommonButton
            title="Move Row Down"
            label=""
            symbol={faArrowDown}
            onClick={() => moveRow(index, index + 1)}
            disabled={index === rows.length - 1}
            variant="arrow"
            size="small"
          />
        </div>
      )
    }
  ];

  // Prepare data for TableComponent
  const tableData = rows.map((row, index) => ({
    ...row,
    index: row.index ?? index,
    label: row.title || '',
    logic: row.Logic || '',
    factor: row.factor || '',
    baseText: row.baseText || '',
    select: selectedRows.includes(row.index ?? index)
  }));

  const tableContent = (
    <div className="table-container">
{/* === NET CREATION SECTION - MOVED TO TOP === */}
  {selectedRows.length > 0 && (
    <div className="net-creation-section">
      <div className="net-label-wrapper">
        <span className="net-prefix">NET:</span>
        <input
          type="text"
          placeholder="Enter custom net label (optional)"
          value={netLabel.replace(/^NET:\s*/, '')} // strip prefix for editing
          onChange={(e) => setNetLabel("NET: " + e.target.value)}
          className="net-input"
        />
      </div>
      <CommonButton 
        label={`Create Net (${selectedRows.length})`}
        onClick={() => {
          const finalLabel = netLabel.trim() || "NET: " + selectedRowsLabel.map(r => r.title).join(' / ');
          createNet(finalLabel, selectedRows);
          setSelectedRows([]);
          setSelectedRowsLabel([]);
          setNetLabel("NET: ");
        }}
        variant="success"
        size="medium"
      />
    </div>
  ) }          
          {/* Action Buttons Section */}
          <div className="action-buttons-section">
            {selectedRows.length > 0 && (
              <>
                <CommonButton 
                  title="Mark Selected Items as Exclusive"
                  label={`Mark Exclusive`}
                  onClick={handleExclusive}
                  variant="warning"
                  size="small"
                />
                <span className="selection-counter">
                  {selectedRows.length} selected
                </span>
              </>
            )}
            
            <div className="checkbox-section">
              <CommonButton
                title={textFormatMode
                  ? "Parse the text/object data below and generate the table"
                  : "Convert the table to a copy-pasteable text/object format"}
                label={textFormatMode ? "Table Format" : "Text Format"}
                onClick={handleTextFormatToggle}
                variant="secondary"
                size="small"
              />
              <input
                type="checkbox"
                id="reverseLabels"
                checked={reverseLabels}
                onChange={(e) => setReverseLabels(e.target.checked)}
              />
              <label htmlFor="reverseLabels">
                Reverse {typeofproperty === 'rows' ? 'Rows' : 
                typeofproperty === 'cols' ? 'Columns' : 
                typeofproperty === 'values' ? 'Values' : 
                'Value Labels'}
              </label>
            </div>
          </div>
          
          {/* Single TableComponent with all functionality, or its Text Format equivalent */}
          <div className="single-table-section">
            {textFormatMode ? (
              <div className="text-format-section">
                <textarea
                  className="text-format-textarea"
                  value={textFormatValue}
                  onChange={handleTextFormatChange}
                  spellCheck={false}
                  rows={Math.max(6, safeRows.length + 1)}
                  placeholder="Edit or paste Text Format data here, then click Table Format to apply it..."
                />
                {textFormatError && (
                  <div className="text-format-error">{textFormatError}</div>
                )}
              </div>
            ) : (
              <TableComponent
                title={`${typeofproperty === 'rows' ? 'Rows' :
                        typeofproperty === 'cols' ? 'Columns' :
                        typeofproperty === 'values' ? 'Values' :
                        typeofproperty === 'statements' ? 'Statements' :
                        'Value Labels'} Details`}
                items={tableData}
                itemType={typeofproperty}
                columns={columns}
                getCurrentLogic={() => ''}
                handleLogicChange={(index, value) => handleLogicChange(index, value)}
                handlePropertyChange={handleTablePropertyChange}
                isGridType={false}
                showAllProperties={false}
              />
            )}
          </div>
        </div>
  );

  if (inline) {
    return <div className="inline-modal-panel">{tableContent}</div>;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {tableContent}
      </div>
    </div>
  );
};

export default TableSettingModal;